import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Target, Briefcase, Award, CheckCircle, Flame, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  bronze: 'bg-amber-600',
  silver: 'bg-gray-400',
  gold: 'bg-yellow-500',
  diamond: 'bg-cyan-500'
};

const ParticipantProfileModal = ({ participantId, isOpen, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && participantId) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, participantId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/participants/${participantId}/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-3xl max-h-[85vh] overflow-y-auto">
        {loading ? (
          <div className="py-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        ) : profile ? (
          <div className="space-y-6">
            {/* Profile Header */}
            <div className="text-center pt-4">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center text-5xl shadow-lg"
              >
                {profile.avatar_emoji}
              </motion.div>
              <h2 className="font-heading text-2xl font-bold">{profile.name}</h2>
              {profile.work_site && (
                <p className="text-muted-foreground flex items-center justify-center gap-1 mt-1">
                  <Briefcase className="w-4 h-4" />
                  {profile.work_site}
                </p>
              )}
              {profile.career_industries?.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {profile.career_industries.map((industry, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {industry}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-3 text-center rounded-2xl bg-gradient-to-br from-yellow-50 to-amber-50">
                <Trophy className="w-6 h-6 text-yellow-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-yellow-700">{profile.total_points}</p>
                <p className="text-xs text-yellow-600">Points</p>
              </Card>
              <Card className="p-3 text-center rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50">
                <Target className="w-6 h-6 text-green-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-700">{profile.goals_completed_count}</p>
                <p className="text-xs text-green-600">Goals Done</p>
              </Card>
              <Card className="p-3 text-center rounded-2xl bg-gradient-to-br from-orange-50 to-red-50">
                <Flame className="w-6 h-6 text-orange-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-orange-700">{profile.current_streak}</p>
                <p className="text-xs text-orange-600">Day Streak</p>
              </Card>
            </div>

            {/* Rank */}
            <Card className="p-4 rounded-2xl bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-center">
              <p className="text-sm opacity-90">SYEP Rank</p>
              <p className="text-4xl font-heading font-bold">#{profile.rank}</p>
            </Card>

            {/* Badges */}
            {profile.badges?.length > 0 && (
              <div>
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Badges Earned ({profile.badges_count})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.badges.map((badge) => (
                    <div
                      key={badge.id}
                      className={`${TIER_COLORS[badge.tier]} text-white px-3 py-2 rounded-full flex items-center gap-2`}
                      title={badge.description}
                    >
                      <span className="text-lg">{badge.icon}</span>
                      <span className="text-sm font-medium">{badge.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Goals */}
            {profile.completed_goals?.length > 0 && (
              <div>
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Completed Goals
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {profile.completed_goals.map((goal) => (
                    <div key={goal.id} className="bg-green-50 rounded-xl p-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{goal.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {goal.goal_type === 'long_term' ? '🎯 Long-term' : '☀️ Summer'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">Profile not found</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ParticipantProfileModal;
